import React, { useState } from 'react';
import { Button, Field, Input, Modal, Note } from '../../../components/app2';

/**
 * Alta de un administrador de empresa (rediseño jul-2026).
 *
 * Mismos tres campos y las mismas validaciones; el molde pasa al modal del
 * sistema y el aviso de la contraseña automática se conserva como nota.
 *
 * El formulario vive en el cuerpo del modal y el botón de guardar lo envía por
 * `form=`, así que el Enter dentro de un campo sigue creando la cuenta.
 */

const FORM_ID = 'create-company-admin-form';

const CreateAdminModal = ({ isOpen, onClose, onSubmit, isLoading, companyName }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear specific field error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // First name validation
    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    } else if (formData?.firstName?.trim()?.length < 2) {
      newErrors.firstName = 'El nombre debe tener al menos 2 caracteres';
    }

    // Last name validation
    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    } else if (formData?.lastName?.trim()?.length < 2) {
      newErrors.lastName = 'El apellido debe tener al menos 2 caracteres';
    }

    // Email validation
    if (!formData?.email?.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex?.test(formData?.email?.trim())) {
        newErrors.email = 'El formato del correo electrónico es inválido';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit({
        firstName: formData?.firstName?.trim(),
        lastName: formData?.lastName?.trim(),
        email: formData?.email?.trim()?.toLowerCase(),
      });

      // Reset form on success (parent will close modal)
      setFormData({ firstName: '', lastName: '', email: '' });
      setErrors({});
    } catch (error) {
      console.error('Error in form submission:', error);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (isLoading) return; // Prevent closing while loading

    setFormData({ firstName: '', lastName: '', email: '' });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      open
      onClose={handleClose}
      title="Agregar administrador"
      subtitle={companyName || 'Empresa'}
      maxWidth={560}
      footer={(
        <>
          <Button variant="outline" size="sm" type="button" onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button size="sm" type="submit" form={FORM_ID} icon="person_add" loading={isLoading}>
            Crear administrador
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        {errors?.submit ? (
          <Note tone="danger" style={{ marginBottom: 16 }}>{errors.submit}</Note>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 14 }}>
          <Field label="Nombre *" htmlFor="new-admin-first" error={errors?.firstName}>
            <Input
              id="new-admin-first"
              type="text"
              value={formData?.firstName}
              onChange={(e) => handleInputChange('firstName', e?.target?.value)}
              placeholder="Nombre"
              disabled={isLoading}
              maxLength={50}
            />
          </Field>

          <Field label="Apellido *" htmlFor="new-admin-last" error={errors?.lastName}>
            <Input
              id="new-admin-last"
              type="text"
              value={formData?.lastName}
              onChange={(e) => handleInputChange('lastName', e?.target?.value)}
              placeholder="Apellido"
              disabled={isLoading}
              maxLength={50}
            />
          </Field>
        </div>

        <Field
          label="Correo electrónico *"
          htmlFor="new-admin-email"
          error={errors?.email}
          hint={errors?.email ? undefined : 'Ahí llegará la contraseña temporal.'}
          style={{ marginTop: 14 }}
        >
          <Input
            id="new-admin-email"
            type="email"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            placeholder="admin@empresa.com"
            disabled={isLoading}
            maxLength={100}
          />
        </Field>

        <Note tone="info" icon="key" style={{ marginTop: 18 }}>
          <strong>Qué pasa al guardar:</strong> se crea la cuenta con acceso a
          {companyName ? ` ${companyName}` : ' la empresa'}, se genera una contraseña segura y se
          envía por correo. Tendrá que cambiarla en su primer inicio de sesión.
        </Note>
      </form>
    </Modal>
  );
};

export default CreateAdminModal;
